import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import UploadedAttachmentList from "../../components/UploadedAttachmentList";
import logo from "../../images/logo.png";

const TASK_ID = 64;
const TASK_NAME = "training_completion_checklist";

// <p> on the top (exact text requested)
const TOP_DESCRIPTION = `New City and Highway Driver Introduction: Training Checklist - Please ensure that each section is covered and a checkmark is put beside all the points reviewed to show it was actually covered.`;

// ============================
// ✅ 42 CHECKBOX ITEMS (EXACT)
// ============================

// Section 1: Introduce/explain the New Employee Practical Training Package.
const SECTION_1 = [
  "Introduce new employee to their driver evaluator - ensure Driver Road Test is done or is completed on 2-3 trips.",
  "Review Job Specific Hazard Assessment - Use the Highway Driver in the Incident & Hazard management system.",
  "Ensure the Health and Safety Policy is reviewed and signed off by the new team member and a copy is kept in personnel file.",
  "Review Key policies - i.e. collision/injury reporting procedures, speed policy, Drug Testing and Security. Step them through the Policies and have them signed off.",
  "Benefit package",
  "Explain Company Service guarantees (before noon delivery, pick-up, etc.).",
];

// Section 2: Local Orientation
const SECTION_2 = [
  "Introduce to key personnel - who to contact if questions arise locally (loads, paperwork, mechanical problems with trailers, etc.).",
  "Tour of facilities - where to pick up paperwork, washrooms, first aid room, locker room, fire extinguishers, emergency exits/muster points.",
  "Parking - where to park their personal vehicle & where to find empty and full trailers.",
  "Review Job/Expectations - proper pre/post trips; how to fill out pre-trip book, trailer defect sheets (Cry Sheets), out of service tags & locations; Prevention Safety & Claims goals & standards.",
  "Explain how the Collision Prevention/Health and Safety Committee works (guidelines and team members/peers are part of the committee).",
  "Proper pre/post-trips are conducted at all times. Go through details before first student trip.",
  "Dress (Steel-toed boots, proper dress, gloves) and discuss the importance employee image plays in customer service.",
  "Dependability - ensure they understand it is their responsibility to be on time or contact us.",
  "Attitude (resolving problems/concerns).",
  "Recording time/Payroll and authorization of Overtime (Trip sheets) - Picture ID worn at all times when on duty.",
  "Emergency evacuation procedure - Review terminal plan in the event of an emergency when they are on the premises.",
  "Show them the Trip Sheets and scanning: how it works and what everything on it means.",
  "Where to find special service stickers, DG stickers; ensure we offer the service with dispatch before committing the service to our customer.",
];

// Section 3: Safe Working habits
const SECTION_3 = [
  "Watching for pinch points when using converters or conducting a pre/post-trip.",
  "Discuss proper lifting techniques; using converters; working with all equipment and freight.",
  "Watch when opening trailer/overhead doors - always test first to make sure they are not jarred.",
  "Don't jump off the dock - use ladders or stairs.",
];

// Section 4: Working with Trailers - Explain the importance of:
const SECTION_4 = [
  "Ensure trailer is properly braced (Anti-nosedive legs/jack stand) and show how they work.",
  "Chock blocks - company policy when units are unattended.",
  "When opening trailer door(s) - watch for freight that could fall.",
  "Ensure heater or reefer are operating if required; show how they work, how to pre-trip and set the temperature.",
  "Check all loads whenever possible before leaving the yard to ensure they are secured properly.",
  "Check trailer numbers to be sure they are correct before hooking up or leaving the yard.",
  "Ensure all DG paperwork is proper before starting trip. Show how a live load release works.",
  "Check for proper placarding procedures and removal of placards when unloaded.",
  "Show them how the Trip Sheets/Accessorial Sheets and BOL/POD works; where to find them; splitting procedure; sending electronically.",
  "Cargo Securement Training: Straps/Working Load Limits must be shown / Proper Axle Weights.",
  "Driver Pre-Tests for FMCSA and Safety quizzes for Winter Driving/CTPAT.",
  "5th wheels - difference between Holland and Fontaine; how to ensure properly locked; review training bulletins and conduct practically.",
  "Bring them to a power tailgate unit; show exactly how it works; have them operate it.",
  "Show them all the different tractors that we use and the differences between them and what to remember in regards to what tractors can pull what equipment, i.e. some tractors can not pull because of the turning radius - can damage dolly legs etc. Point these out.",
  "Air ride trailers - how to drain air in suspension (Air Bags) when backing into dock; ensure entirely drained; explain slide-ahead risk if not drained.",
  "Show them where the insurance and registration is found in all units including trailers (sliding receptacle on front of trailer).",
  "Working with A-Trains: correct procedure for hooking a pair of trailers together; converters and pinch points involved.",
  "Working with Sliding Suspensions: show how to slide the bogeys; routine and correct procedure; yard layout (full loads, empty line up, etc.).",
  "Ensure the Driver knows how to release frozen brakes in winter and where to strike the hammer when releasing.",
];

// ✅ Validate we have exactly 42 items (6 + 13 + 4 + 19 = 42)
const ALL_ITEMS = [...SECTION_1, ...SECTION_2, ...SECTION_3, ...SECTION_4];

const makeInitialAnswers = () =>
  ALL_ITEMS.map((label, idx) => ({
    key: `tcc_${idx + 1}`,
    label,
    value: false,
  }));

export default function TrainingCompletionChecklist() {
  const navigate = useNavigate();
  const { id: paramId } = useParams();

  // same admin/user selection logic as SIN Card
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const [isSigned, setIsSigned] = useState(false);
  const [answers, setAnswers] = useState(makeInitialAnswers());

  const [file, setFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [originalFileName, setOriginalFileName] = useState(null);

  const disabledForUser = !isAdmin && isSigned;

  const resetForm = useCallback(() => {
    setAnswers(makeInitialAnswers());
    setFile(null);
    setUploadedFileName(null);
    setOriginalFileName(null);
  }, []);

  const groupedIndexMap = useMemo(() => {
    // Precompute section start indices so numbering stays global 1..42
    const s1Start = 0;
    const s2Start = SECTION_1.length;
    const s3Start = SECTION_1.length + SECTION_2.length;
    const s4Start = SECTION_1.length + SECTION_2.length + SECTION_3.length;
    return { s1Start, s2Start, s3Start, s4Start };
  }, []);

  const fetchChecklist = useCallback(async () => {
    if (!userId) return;

    try {
      const res = await axios.get(
        `${API_BASE}/api/training-checklist/${userId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );

      setUploadedFileName(res.data?.tcc_file || null);
      setOriginalFileName(res.data?.tcc_original || null);

      const serverAnswers = res.data?.tcc_answers;
      if (Array.isArray(serverAnswers) && serverAnswers.length === 42) {
        setAnswers(serverAnswers);
      } else {
        resetForm();
      }
    } catch {
      resetForm();
    }
  }, [API_BASE, userId, resetForm]);

  useEffect(() => {
    fetchChecklist();
  }, [fetchChecklist]);

  // same signed-state logic as SIN Card (review/completed locks user edits)
  useEffect(() => {
    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        const status =
          res.data?.status?.[String(TASK_ID)] ??
          res.data?.status?.[TASK_NAME] ??
          "not_completed";

        setIsSigned(status === "completed");
      } catch {
        setIsSigned(false);
      }
    };

    if (userId) fetchTaskStatus();
  }, [API_BASE, userId]);

  const toggleByAbsoluteIndex = (absIdx) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[absIdx] = { ...next[absIdx], value: !next[absIdx].value };
      return next;
    });
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    // SIN-style allowed types
    const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "application/vnd.ms-outlook", "application/x-msg", "application/CDFV2", "application/x-ole-storage", "application/octet-stream", ""];
    if (!allowedTypes.includes(selected.type)) {
      alert("Incorrect file format.");
      return;
    }
    setFile(selected);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Ensure exactly 42 items always saved
    if (!Array.isArray(answers) || answers.length !== 42) {
      alert("Checklist must contain exactly 42 items.");
      return;
    }

    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("tcc_answers", JSON.stringify(answers));
    if (file) formData.append("file", file);

    const response = await axios.post(
      `${API_BASE}/api/training-checklist/upload?userId=${userId}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
        },
      },
    );

    alert(response.data?.message || "Saved");
    fetchChecklist();
  };

  const renderSection = (title, items, startIndex) => {
    return (
      <div className="p-3 bg-light rounded border mb-3">
        <div className="fw-semibold mb-2" style={{ fontSize: 16 }}>
          {title}
        </div>

        <div className="row">
          {items.map((label, idx) => {
            const absIdx = startIndex + idx;
            const item = answers[absIdx];

            return (
              <div className="col-md-6 mb-2" key={item?.key || `tmp_${absIdx}`}>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={item?.key || `tcc_${absIdx + 1}`}
                    checked={!!item?.value}
                    onChange={() => toggleByAbsoluteIndex(absIdx)}
                    disabled={disabledForUser}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={item?.key || `tcc_${absIdx + 1}`}
                  >
                    {label}
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Header (SIN style) */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            Training Completion Checklist
          </span>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        {/* Logo/Title/Address (SIN style) */}
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Training Completion Checklist</h2>
          </div>
          <CompanyAddress />
        </div>

        {/* Top paragraph */}
        <p style={{ whiteSpace: "pre-line" }} className="text-muted">
          {TOP_DESCRIPTION}
        </p>

        <form onSubmit={handleSubmit}>
          {/* SECTION 1 */}
          {renderSection(
            "Introduce/explain the New Employee Practical Training Package.",
            SECTION_1,
            groupedIndexMap.s1Start,
          )}

          {/* <div className="mb-3">
            <div className="fw-semibold">Benefit package</div>
          </div> */}
          {/* SECTION 2 */}
          {renderSection(
            "Local Orientation",
            SECTION_2,
            groupedIndexMap.s2Start,
          )}
          {/* SECTION 3 */}
          {renderSection(
            "Safe Working habits",
            SECTION_3,
            groupedIndexMap.s3Start,
          )}
          {/* SECTION 4 */}
          {renderSection(
            "Working with Trailers - Explain the importance of:",
            SECTION_4,
            groupedIndexMap.s4Start,
          )}
          <div className="row">
            <div className="col-md-12 mb-3">
              <label>Select File (Optional)</label>
              <input
                type="file"
                className="form-control"
                onChange={handleFileChange}
                disabled={disabledForUser}
              />
            </div>
          </div>
          {/* Uploaded version box (SIN style) */}
          {uploadedFileName && (
            <UploadedAttachmentList
              title="Uploaded Version"
              files={[
                {
                  fileName: originalFileName || "View file",
                  filePath: uploadedFileName,
                },
              ]}
              getFileKey={(file) => file.filePath}
              getFileName={(file) => file.fileName}
              getFileViewUrl={(file) =>
                `${API_BASE}/uploads/training_checklist/${file.filePath}`
              }
              getFileDownloadName={(file) => file.fileName}
            />
          )}
          <div className="mt-4 d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={disabledForUser}
            >
              Save
            </button>
          </div>
        </form>

        <hr className="my-5" />

        {/* Signature + Review blocks (SIN style) */}
        {userId && (
          <>
            <SignatureManager
              taskName={TASK_NAME}
              taskId={TASK_ID}
              userId={isAdmin && paramId ? paramId : userId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName={TASK_NAME}
              taskId={TASK_ID}
              userId={isAdmin && paramId ? paramId : userId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
}



